import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Hourglass, UserX } from "lucide-react";
import { Navigate } from "react-router";

import { Button, EmptyState } from "@/components";
import { authApi } from "@/features/mail/api/client";

/**
 * 승인을 기다리는 사람이 보는 화면.
 *
 * 로그인 자체를 막지 않는 이유가 여기 있다. 막아버리면 자기가 **기다리는 중인지
 * 거절당했는지** 알 방법이 없어서 계속 다시 가입하려 들고, 그때마다 관리자에게
 * 같은 신청이 쌓인다. 상태를 보여주면 기다린다.
 *
 * 다시 부르는 것은 새로고침뿐이다. 승인은 사람이 하는 일이라 몇 초 만에 오지
 * 않는데 폴링을 걸면 그동안 계속 서버를 두드리게 된다.
 */
export function PendingPage() {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ["me"], queryFn: authApi.me, retry: false });

  const signOut = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => queryClient.clear(),
  });

  if (meQuery.isError) return <Navigate to="/login" replace />;
  // 승인이 떨어졌거나, 서버가 승인 개념을 모르는 옛 API면 여기 있을 이유가 없다.
  if (meQuery.isSuccess && meQuery.data.status !== "pending" &&
      meQuery.data.status !== "rejected") {
    return <Navigate to="/" replace />;
  }

  const rejected = meQuery.data?.status === "rejected";

  return (
    <div className="bg-paper flex h-full items-center justify-center">
      {rejected ? (
        <EmptyState
          size="md"
          icon={<UserX />}
          title="가입이 받아들여지지 않았어요"
          description="이 주소로는 시작할 수 없어요. 잘못된 것 같으면 알려주세요."
          actions={
            <Button size="sm" variant="outline" onClick={() => signOut.mutate()}>
              로그아웃
            </Button>
          }
        />
      ) : (
        <EmptyState
          size="md"
          icon={<Hourglass />}
          title="승인을 기다리는 중이에요"
          // 언제 되는지 모르면서 "곧"이라고 하지 않는다. 사람이 보고 여는 일이다.
          description={`${meQuery.data?.primaryAddress ?? ""} 으로 신청했어요. 확인되면 이 주소로 바로 메일을 받을 수 있어요.`}
          actions={
            <>
              <Button size="sm" onClick={() => void meQuery.refetch()}>
                다시 확인
              </Button>
              <Button size="sm" variant="ghost" onClick={() => signOut.mutate()}>
                로그아웃
              </Button>
            </>
          }
        />
      )}
    </div>
  );
}
